import { useCallback } from 'react';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { Merchant } from '@frontend/api-payments';
import { PersonTypes, PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { Modal, ModalControlModalProps, useAlert } from '@frontend/design-system';
import { PaymentMethods } from '../widgets/payment-methods';

export type GenericPaymentMethodsModalProps = {
  title?: React.ReactNode;
  merchant?: Merchant;
  person?: PersonTypes.Person;
  locationId: string | undefined;
  modalProps: ModalControlModalProps;
  minWidth?: number;
  maxWidth?: number;
  onClose?: () => void;
  onError?: (message: string, err?: Error) => void;
  onSelectPaymentMethod: (pm: PaymentMethod) => void;
  onVerificationEmailSent?: () => void;
};
export const GenericPaymentMethodsModal = ({
  title,
  person,
  merchant,
  locationId,
  minWidth = 600,
  maxWidth = 600,
  modalProps,
  onError,
  onClose,
  onSelectPaymentMethod,
  onVerificationEmailSent,
}: GenericPaymentMethodsModalProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const onErrorCallback = useCallback(
    (message: string, err?: Error) => {
      if (onError) {
        onError(message, err);
        return;
      }

      if (err) {
        console.warn('Caught error from payment methods widget', err);
      }
      if (message) {
        alerts.error(message);
      } else {
        alerts.error('An unknown error occurred while attempting to select a payment method.');
      }
    },
    [alerts, onError]
  );

  const onVerificationEmailSentCallback = useCallback(() => {
    if (onVerificationEmailSent) {
      onVerificationEmailSent();
      return;
    }
    alerts.success(t('ACH verification email sent successfully'));
  }, [alerts, onVerificationEmailSent]);

  const onCloseCallback = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    modalProps.onClose();
  }, [modalProps, onClose]);

  const convertedPerson = PersonsV3.PersonHelpers.convertPersonToPersonV3(person, true);

  return (
    <Modal className='generic-payment-methods-modal' {...modalProps} minWidth={minWidth} maxWidth={maxWidth}>
      <PaymentMethods
        locationId={locationId || ''}
        merchant={merchant}
        person={convertedPerson}
        onCancel={onCloseCallback}
        onError={onErrorCallback}
        onPaymentMethodSelectedSuccess={onSelectPaymentMethod}
        onVerificationEmailSent={onVerificationEmailSentCallback}
        showCancelButtonOnList={true}
      >
        {title !== false && <Modal.Header>{title ?? t('Payment Methods on File')}</Modal.Header>}
        <Modal.Body>
          <PaymentMethods.View view='list'>
            <PaymentMethods.List />
          </PaymentMethods.View>

          <PaymentMethods.View view='add-card'>
            <PaymentMethods.RelativeContainer>
              <PaymentMethods.AddCardForm />
            </PaymentMethods.RelativeContainer>
          </PaymentMethods.View>

          <PaymentMethods.View view='add-ach'>
            <PaymentMethods.RelativeContainer>
              <PaymentMethods.AchForm />
            </PaymentMethods.RelativeContainer>
          </PaymentMethods.View>
        </Modal.Body>
        <PaymentMethods.FooterActions />
      </PaymentMethods>
    </Modal>
  );
};
